'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Coffee, HeartPulse, ShoppingBag, Briefcase,
    Scissors, FileSpreadsheet, Building2, GraduationCap,
    ArrowRight, Search, MapPin,
} from 'lucide-react';
import { EASE_CUSTOM } from '@/lib/animations'
import QuickLeadModal from './ui/QuickLeadModal';
import { Button } from './ui/button';

/* ── Sectores: todos al mismo nivel ── */
const niches = [
    { id: 'hosteleria', label: 'Hostelería', icon: Coffee, color: 'amber' },
    { id: 'construccion', label: 'Construcción', icon: Building2, color: 'orange' },
    { id: 'gestorias', label: 'Gestorías', icon: FileSpreadsheet, color: 'blue' },
    { id: 'clinicas', label: 'Clínicas', icon: HeartPulse, color: 'rose' },
    { id: 'formacion', label: 'Formación', icon: GraduationCap, color: 'indigo' },
    { id: 'ecommerce', label: 'E-commerce', icon: ShoppingBag, color: 'teal' },
    { id: 'peluqueria', label: 'Peluquerías', icon: Scissors, color: 'pink' },
    { id: 'otros', label: 'Otros', icon: Briefcase, color: 'slate' },
];

const colorMap = {
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', ring: 'ring-amber-300/40', dot: 'bg-amber-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', ring: 'ring-blue-300/40', dot: 'bg-blue-500' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', ring: 'ring-orange-300/40', dot: 'bg-orange-500' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'text-rose-600', ring: 'ring-rose-300/40', dot: 'bg-rose-500' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', ring: 'ring-indigo-300/40', dot: 'bg-indigo-500' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'text-teal-600', ring: 'ring-teal-300/40', dot: 'bg-teal-500' },
    pink: { bg: 'bg-pink-50', border: 'border-pink-200', icon: 'text-pink-600', ring: 'ring-pink-300/40', dot: 'bg-pink-500' },
    slate: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600', ring: 'ring-slate-300/40', dot: 'bg-slate-500' },
};

const diagnosticItems = [
    'Cómo trabajas día a día y qué herramientas usas',
    'Dónde pierdes tiempo y dinero cada semana',
    'Qué procesos manuales podrían automatizarse',
    'Qué métricas necesitas para tomar decisiones',
];

export default function NichoSelector() {
    const [active, setActive] = useState(niches[0]);
    const [modalOpen, setModalOpen] = useState(false);

    return (
        <>
        <QuickLeadModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            context={`${active.label} — Quiero mi diagnóstico`}
        />
        <section id="nicho-selector" className="py-20 md:py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">

                {/* Header */}
                <div className="mb-10">
                    <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">
                        Consultoría tecnológica para tu negocio
                    </div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Cada negocio trabaja distinto.
                        <br />
                        <span className="text-slate-400 font-light">Lo entendemos antes de proponer.</span>
                    </h2>
                    <p className="text-lg text-slate-500 font-light max-w-2xl">
                        Trabajamos con cualquier tipo de negocio en España. Primero analizamos cómo funcionas,
                        qué herramientas usas y dónde se te escapa el tiempo. Después proponemos. Nunca al revés.
                    </p>
                </div>

                {/* ── Sector grid (all equal level) ── */}
                <div className="mb-6">
                    <p className="text-xs font-mono tracking-[0.25em] text-slate-400 uppercase mb-3.5">
                        Selecciona tu sector
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                        {niches.map((niche) => {
                            const c = colorMap[niche.color as keyof typeof colorMap];
                            const Icon = niche.icon;
                            const isActive = active.id === niche.id;
                            return (
                                <button
                                    key={niche.id}
                                    onClick={() => setActive(niche)}
                                    className={`group flex items-center gap-2.5 sm:gap-3 rounded-2xl border px-3 py-3 sm:px-4 sm:py-3.5 text-left transition-all duration-200 min-w-0 ${
                                        isActive
                                            ? `${c.bg} ${c.border} ring-2 ${c.ring} shadow-sm`
                                            : 'border-slate-200/60 bg-white/50 hover:bg-white/80 hover:border-slate-300 backdrop-blur-sm'
                                    }`}
                                >
                                    <div className={`w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-xl flex items-center justify-center ${isActive ? c.bg : 'bg-slate-100'} border ${isActive ? c.border : 'border-slate-200'}`}>
                                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? c.icon : 'text-slate-500'}`} />
                                    </div>
                                    <span className={`text-sm sm:text-[15px] font-bold truncate ${isActive ? 'text-slate-900' : 'text-slate-600'}`}>
                                        {niche.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── Universal diagnostic panel ── */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={active.id}
                        initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
                        transition={{ duration: 0.35, ease: EASE_CUSTOM }}
                        className="glass-card rounded-3xl p-7 sm:p-9 md:p-10"
                    >
                        {(() => {
                            const c = colorMap[active.color as keyof typeof colorMap];
                            const Icon = active.icon;
                            return (
                                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-start">
                                    {/* Left: diagnostic content */}
                                    <div>
                                        <div className="inline-flex items-center gap-2 mb-5">
                                            <div className={`w-9 h-9 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center`}>
                                                <Icon className={`w-4 h-4 ${c.icon}`} />
                                            </div>
                                            <span className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500">
                                                Diagnóstico para {active.label.toLowerCase()}
                                            </span>
                                        </div>

                                        <h3 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 mb-3 leading-snug">
                                            Analizamos cómo trabaja tu negocio.
                                            <br />
                                            <span className="text-slate-400 font-light">Detectamos lo que no funciona.</span>
                                        </h3>

                                        <p className="text-slate-500 font-light leading-relaxed mb-6">
                                            No vendemos paquetes cerrados ni soluciones genéricas. Cada negocio tiene sus propios cuellos de botella —
                                            nuestro trabajo es encontrarlos y resolverlos con la tecnología adecuada para ti.
                                        </p>

                                        <div className="mb-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Search className={`w-4 h-4 ${c.icon}`} />
                                                <span className="text-sm font-bold text-slate-800">En el diagnóstico analizamos:</span>
                                            </div>
                                            <ul className="flex flex-col gap-2.5">
                                                {diagnosticItems.map((item) => (
                                                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${c.dot}`} />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className={`${c.bg} border ${c.border} rounded-xl p-4 mb-6`}>
                                            <p className="text-sm font-medium text-slate-700">
                                                <span className="font-bold">Resultado en 48h:</span>{' '}
                                                Un informe con los problemas reales detectados y un plan claro para resolverlos.
                                            </p>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <Button asChild size="default">
                                                <a
                                                    href="https://calendly.com/innovatheratech/30min"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="group"
                                                >
                                                    Pedir diagnóstico gratuito
                                                    <ArrowRight className="group-hover:translate-x-0.5 transition-transform" />
                                                </a>
                                            </Button>
                                            <Button variant="secondary" size="default" onClick={() => setModalOpen(true)}>
                                                Ya sé lo que necesito
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Right: process preview card (desktop) */}
                                    <div className="hidden lg:flex w-[280px] shrink-0 flex-col gap-3 rounded-2xl border border-slate-200/60 bg-white/60 backdrop-blur-sm p-5">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                                            <span className="text-[10px] font-mono tracking-[0.2em] text-slate-400 uppercase">
                                                Proceso
                                            </span>
                                        </div>
                                        {[
                                            { n: '01', t: 'Análisis 30 min', d: 'Por videollamada' },
                                            { n: '02', t: 'Informe en 48h', d: 'Con propuesta concreta' },
                                            { n: '03', t: 'Decisión tuya', d: 'Sin compromiso' },
                                        ].map(s => (
                                            <div key={s.n} className="flex gap-3 items-start">
                                                <span className={`text-[10px] font-mono font-bold ${c.icon} mt-0.5`}>{s.n}</span>
                                                <div className="min-w-0">
                                                    <p className="text-[13px] font-semibold text-slate-800 leading-tight">{s.t}</p>
                                                    <p className="text-[11px] text-slate-400 mt-0.5">{s.d}</p>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="mt-1 pt-3 border-t border-slate-200/60 flex items-center gap-1.5">
                                            <MapPin className="w-3 h-3 text-slate-400" />
                                            <span className="text-[10px] text-slate-400 font-mono tracking-wide">Toda España · 100% online</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </motion.div>
                </AnimatePresence>

                {/* ── Bottom note ── */}
                <p className="mt-6 text-center text-xs text-slate-400 font-mono tracking-wide">
                    ¿Tu sector no está en la lista? Da igual — trabajamos con cualquier tipo de negocio.
                </p>
            </div>
        </section>
        </>
    );
}
